import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #couponObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('click', this.#handleLoginReturnClick);
    this.#populateCoupon();
    this.#couponObserver = new MutationObserver(() => this.#populateCoupon());
    this.#couponObserver.observe(this, { childList: true, subtree: true, characterData: true });
    this.#maybeOpenFromQuery();
  }

  #maybeOpenFromQuery = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('is_cart_modal') !== 'true') return;
    params.set('is_cart_modal', 'false');
    const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    window.history.replaceState({}, '', newUrl);
    requestAnimationFrame(() => this.open());
  };

  /**
   * 드로어 안 로그인 CTA(data-cart-login-return)의 return_to 를 "보던 지면 + is_cart_modal=true" 로 채운다.
   * 로그인 후 돌아오면 #maybeOpenFromQuery 가 드로어를 다시 연다.
   * 클릭 시점에 채우는 이유: 드로어는 Section Rendering API 로 다시 그려져 서버의 request.path 를 믿을 수 없다.
   * (마크업 폴백은 snippets/cart-shipment-timer.liquid 참고)
   */
  #handleLoginReturnClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-cart-login-return]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    const params = new URLSearchParams(window.location.search);
    params.set('is_cart_modal', 'true');
    const loginUrl = new URL(link.href, window.location.origin);
    loginUrl.searchParams.set('return_to', `${window.location.pathname}?${params.toString()}`);
    link.href = loginUrl.toString();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('click', this.#handleLoginReturnClick);
    this.#couponObserver?.disconnect();
    this.#couponObserver = null;
  }

  #populateCoupon = () => {
    const source = document.querySelector('div[data-coupon-text]');
    if (!source) return;
    const text = source.getAttribute('data-coupon-text') || '';
    const code = source.getAttribute('data-coupon-code') || '';
    this.querySelectorAll('.cart-drawer__banner-text').forEach((el) => {
      if (el.textContent !== text) el.textContent = text;
    });
    this.querySelectorAll('.cart-drawer__coupon-code').forEach((el) => {
      if (el.textContent !== code) el.textContent = code;
    });
    // 복사 버튼의 data-code도 헤더 쿠폰 코드로 동기화 — 보이는 코드와 복사되는 코드를 일치시킨다
    this.querySelectorAll('.cart-drawer__copy-code').forEach((el) => {
      if (el.getAttribute('data-code') !== code) el.setAttribute('data-code', code);
    });
  };

  #handleCartAdd = () => {
    // Defer opening until after cart section morph completes
    requestAnimationFrame(() => {
      if (window.matchMedia('(min-width: 750px)').matches) {
        this.open();
      } else {
        const message = Theme.translations.added_to_cart || 'Added to cart';
        window.showToast(message, 3000, { type: 'cart-add' });
      }
    });
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
